import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useProjectScopedEffect } from '../stores/project';
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type Simulation,
  type SimulationNodeDatum,
} from 'd3-force';

import { shaktiApi, ApiError } from '../api/client';
import type { DependencyGraph, GraphEdge, GraphNode } from '../api/types';

/**
 * Dependency Map — FEAT-ADD-030 (closes B4 from the 2026-04-17 release audit).
 *
 * Replaces the prior HTML-list fallback with a real D3 force-directed
 * layout rendered onto `<canvas>` (target: 500+ nodes at 60fps).
 *
 * Canvas + d3-force combo keeps us under the Dashboard SPA's 10 KB
 * initial-bundle target (react-router + react-dom are the big
 * contributors; `d3-force` is tree-shakable and adds ~10 KB gzip).
 *
 * Accessibility: filter controls remain the primary a11y entry
 * point; the canvas is decorative for keyboard users. Respects
 * `prefers-reduced-motion` by fixing the simulation immediately
 * instead of animating.
 */
export function DependenciesRoute(): JSX.Element {
  const [graph, setGraph] = useState<DependencyGraph | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterKind, setFilterKind] = useState('');
  const [filterLabel, setFilterLabel] = useState('');

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const simRef = useRef<Simulation<SimNode, SimEdge> | null>(null);

  // N5.5 / R5 (round-6) — project-scoped graph reload with abort.
  useProjectScopedEffect((signal) => {
    shaktiApi
      .getDependencyGraph({ signal })
      .then(setGraph)
      .catch((e: unknown) => {
        if (signal.aborted) return;
        setError(
          e instanceof ApiError ? e.body?.error ?? e.message : 'Failed to load graph',
        );
      });
  });

  const filteredNodes = useMemo<GraphNode[]>(() => {
    if (!graph) return [];
    return graph.nodes.filter(
      (n) =>
        (!filterKind || n.kind === filterKind) &&
        (!filterLabel || n.label.toLowerCase().includes(filterLabel.toLowerCase())),
    );
  }, [graph, filterKind, filterLabel]);

  const filteredEdges = useMemo<GraphEdge[]>(() => {
    if (!graph) return [];
    const ids = new Set(filteredNodes.map((n) => n.id));
    return graph.edges.filter((e) => ids.has(e.source) && ids.has(e.target));
  }, [graph, filteredNodes]);

  const kinds = useMemo(
    () => Array.from(new Set((graph?.nodes ?? []).map((n) => n.kind))).sort(),
    [graph],
  );

  // Drive the force simulation against the Canvas.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || filteredNodes.length === 0) {
      simRef.current?.stop();
      simRef.current = null;
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = width * devicePixelRatio;
    canvas.height = height * devicePixelRatio;
    ctx.scale(devicePixelRatio, devicePixelRatio);

    const nodes: SimNode[] = filteredNodes.map((n) => ({
      id: n.id,
      label: n.label,
      kind: n.kind,
    }));
    const edges: SimEdge[] = filteredEdges.map((e) => ({ source: e.source, target: e.target }));

    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const sim = forceSimulation<SimNode>(nodes)
      .force('charge', forceManyBody<SimNode>().strength(-120))
      .force('link', forceLink<SimNode, SimEdge>(edges).id((d) => d.id).distance(60))
      .force('center', forceCenter(width / 2, height / 2))
      .force('collide', forceCollide<SimNode>().radius(14));

    if (reduceMotion) {
      // Drain to final layout immediately, then paint once.
      for (let i = 0; i < 300; i++) sim.tick();
      paint(ctx, nodes, edges, width, height);
      sim.stop();
    } else {
      sim.on('tick', () => paint(ctx, nodes, edges, width, height));
      sim.alpha(0.9).restart();
    }

    simRef.current = sim;
    return () => {
      sim.stop();
      simRef.current = null;
    };
  }, [filteredNodes, filteredEdges]);

  return (
    <section aria-labelledby="dep-title" className="mx-auto flex max-w-6xl flex-col gap-4">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/dependencies
        </span>
        <h1 id="dep-title" className="text-2xl font-semibold text-fg-primary">
          Dependency Map
        </h1>
        <p className="text-sm text-fg-secondary">
          {graph
            ? `${graph.nodes.length} nodes, ${graph.edges.length} edges (generated ${new Date(
                graph.generated_at,
              ).toLocaleTimeString()})`
            : 'Loading graph…'}
        </p>
      </header>

      {error ? (
        <div role="alert" className="shakti-card border border-status-error/40 text-status-error">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-fg-muted">
          Kind
          <select
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
          >
            <option value="">(any)</option>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-fg-muted">
          Label contains
          <input
            type="text"
            value={filterLabel}
            onChange={(e) => setFilterLabel(e.target.value)}
            placeholder="search…"
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
          />
        </label>
        <div className="ml-auto text-xs text-fg-muted">
          {filteredNodes.length} / {graph?.nodes.length ?? 0} nodes
        </div>
      </div>

      <div className="shakti-card p-0 overflow-hidden" style={{ height: 520 }} aria-hidden="true">
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
      </div>

      <details className="shakti-card">
        <summary className="cursor-pointer text-xs text-fg-muted">
          Accessible node list ({filteredNodes.length} shown)
        </summary>
        <ul className="mt-2 flex flex-col gap-0.5" aria-label="Graph nodes">
          {filteredNodes.map((n) => (
            <li
              key={n.id}
              className="flex items-center justify-between rounded-sm px-2 py-1 font-mono text-xs text-fg-secondary hover:bg-bg-raised"
            >
              <span>{n.label}</span>
              <span className="text-2xs uppercase text-fg-muted">
                {n.kind} · {countEdges(graph?.edges, n.id)} edges
              </span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

// ── Canvas renderer ────────────────────────────────────────────────────────

interface SimNode extends SimulationNodeDatum {
  id: string;
  label: string;
  kind: string;
}

interface SimEdge {
  source: string | SimNode;
  target: string | SimNode;
}

function paint(
  ctx: CanvasRenderingContext2D,
  nodes: SimNode[],
  edges: SimEdge[],
  width: number,
  height: number,
): void {
  ctx.clearRect(0, 0, width, height);

  // Edges first so nodes draw on top.
  ctx.strokeStyle = 'rgba(200, 200, 220, 0.25)';
  ctx.lineWidth = 1;
  edges.forEach((e) => {
    const s = typeof e.source === 'string' ? null : e.source;
    const t = typeof e.target === 'string' ? null : e.target;
    if (!s || !t || s.x == null || s.y == null || t.x == null || t.y == null) return;
    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    ctx.lineTo(t.x, t.y);
    ctx.stroke();
  });

  // Nodes.
  nodes.forEach((n) => {
    if (n.x == null || n.y == null) return;
    ctx.fillStyle = kindColor(n.kind);
    ctx.beginPath();
    ctx.arc(n.x, n.y, 6, 0, Math.PI * 2);
    ctx.fill();
  });
}

function kindColor(kind: string): string {
  // Deterministic palette; matches design tokens in spirit without
  // reaching into Tailwind (the canvas isn't in the cascade).
  switch (kind) {
    case 'module':
      return '#4fd1c5';
    case 'phase':
      return '#f6ad55';
    case 'artifact':
      return '#9f7aea';
    default:
      return '#cbd5e0';
  }
}

function countEdges(edges: readonly GraphEdge[] | undefined, nodeId: string): number {
  if (!edges) return 0;
  return edges.filter((e) => e.source === nodeId || e.target === nodeId).length;
}
